import type { StateCreator, StoreMutatorIdentifier, State } from 'zustand';

function formatActionFromPartial(partial: unknown): string {
  if (typeof partial !== 'object' || partial === null) return 'unknown';

  const keys = Object.keys(partial);
  const keyCount = keys.length;

  if (keyCount === 0) return 'empty update';

  if (keyCount > 3) {
    const preview = keys.slice(0, 3).join(', ');
    return `${preview}, ... (+${keyCount - 3})`;
  }

  return keys.join(', ');
}

type Logger = <
  T extends State,
  Mps extends [StoreMutatorIdentifier, unknown][] = [],
  Mcs extends [StoreMutatorIdentifier, unknown][] = [],
>(
  f: StateCreator<T, Mps, Mcs>,
  storeName?: string
) => StateCreator<T, Mps, Mcs>;

type LoggerImpl = <T extends State>(
  f: StateCreator<T>,
  storeName?: string
) => StateCreator<T>;

const loggerImpl: LoggerImpl =
  (f, storeName = 'Zustand Store') =>
  (set, get, api) => {
    const loggedSet: typeof set = (
      partial,
      replace,
      actionName = 'anonymous'
    ) => {
      if (process.env.NODE_ENV !== 'production') {
        const prevState = get();
        const action =
          actionName === 'anonymous'
            ? formatActionFromPartial(partial)
            : actionName;

        console.groupCollapsed(
          `%c${storeName} %c→ %c${action}`,
          'color: gray; font-weight: lighter;',
          'color: #aaa;',
          'color: #03A9F4; font-weight: bold;'
        );
        console.log(
          '%cPrevious state:',
          'color: #9E9E9E; font-weight: bold;',
          prevState
        );
        console.log('%cAction:', 'color: #03A9F4;', partial);
      }

      set(partial, replace);

      if (process.env.NODE_ENV !== 'production') {
        const nextState = get();
        console.log(
          '%cNext state:',
          'color: #4CAF50; font-weight: bold;',
          nextState
        );
        console.groupEnd();
      }
    };

    return f(loggedSet, get, api);
  };

export const logger = loggerImpl as unknown as Logger;
